import React, { useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Modal from '../../../components/Modal'
import TestIds from '../../../testIds'
import { useSelector } from 'react-redux'
import { Stack, Theme, useMediaQuery } from '@mui/material'
import {
  NutritionPromoItem,
  PromotionOrderPayload,
  SelectedPromo,
  getNutritionPromoInfo,
  selectPromoConfig,
  selectPromoDictionary,
  selectPromoItems,
} from '../storage'
import Steppers from '../../../components/Stepper/Stepper'
import { Button } from '../../../components/Button'
import { colors } from '../../../themes/colors'
import {
  selectCurrentFarmerAccount,
  selectEmailForPrefill,
  selectNameForPrefill,
  selectPhoneForPrefill,
} from '../../azure-b2c/storage'
import { isInvalidEmail } from '../../../util/validateEmail'
import { isInvalidPhoneNumber } from '../../../util/validatePhoneNumber'
import { StepOne, StepThree, StepTwo } from './ModalSteps'
import apiClient from '../../../services/api-client'
import { useNavigate } from 'react-router-dom'
import { Loader } from '../../../components/Loader'
import { useAppDispatch } from '../../../store/rootStore'
import { AxiosError } from 'axios'

type DeclarationModalProps = {
  open: boolean
  handleClose: () => void
}

const PromoModal = ({ open, handleClose }: DeclarationModalProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const promoItems = useSelector(selectPromoItems)
  const promoDictionary = useSelector(selectPromoDictionary)
  const config = useSelector(selectPromoConfig)
  const account = useSelector(selectCurrentFarmerAccount)
  const uniqueLocations = [
    ...new Set(promoItems.flatMap((e) => e.prices.map((b) => b.location))),
  ].map((location) => ({ value: location }))

  const [selectedDepot, setSelectedDepot] = React.useState(
    account?.defaultPort?.name ? { value: account?.defaultPort?.name } : uniqueLocations[0],
  )

  useEffect(() => {
    if (account) {
      setSelectedDepot({ value: account?.defaultPort?.name })
    }
  }, [account])

  const [selectedItems, setSelectedItems] = React.useState<SelectedPromo[]>([])

  const [pageIndex, setPageIndex] = React.useState(0)
  const [previousIndex, setPreviousIndex] = React.useState<number | undefined>()
  const [pageOneError, setPageOneError] = React.useState<string | undefined>()
  const [pageTwoError, setPageTwoError] = React.useState<string | undefined>()
  const [pageThreeError, setPageThreeError] = React.useState<string | undefined>()

  const [expanded, setExpanded] = React.useState<string[]>([])
  const [acceptedTerms, setAcceptedTerms] = React.useState(false)

  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const accountEmail = useSelector(selectEmailForPrefill)
  const accountUsername = useSelector(selectNameForPrefill)
  const accountPhone = useSelector(selectPhoneForPrefill)
  const [email, setEmail] = React.useState<string | undefined>(accountEmail ?? '')
  const [phone, setPhone] = React.useState<string | undefined>(accountPhone ?? '')
  const [emailError, setEmailError] = React.useState<string>()
  const [phoneError, setPhoneError] = React.useState<string>()
  const [notes, setNotes] = React.useState<string>()

  useEffect(() => {
    const selectedKeys = selectedItems.map((e) => e.key)
    if (expanded.length > 0 && !expanded.some((e) => selectedKeys.includes(e))) {
      setExpanded([])
    }
  }, [selectedItems])

  useEffect(() => {
    // Filter out selected items without matching promo items
    const updatedSelectedItems = selectedItems.filter((selectedItem) => {
      return promoItems.some((promoItem) => promoItem.key === selectedItem.key)
    })

    const updated = updatedSelectedItems.map((selectedItem) => {
      // Find the corresponding promo item
      const matchingPromoItem = promoItems.find((promoItem) => promoItem.key === selectedItem.key)

      // If no matching promo item found, return null
      if (!matchingPromoItem) {
        return null
      }

      // Find the price for the selected depot
      const selectedPrice = matchingPromoItem.prices.find(
        (price) => price.location === selectedDepot.value,
      )
      const updatedPrice = selectedPrice ? selectedPrice.price : null

      // Return updated selectedItem with new price
      return {
        ...selectedItem,
        price: updatedPrice,
      }
    })

    const filtered = updated.filter((e) => e !== null) as SelectedPromo[]

    // Update prices of remaining selected items based on selectedDepot
    setSelectedItems(filtered) // Filter out items that are null
  }, [promoItems])

  useEffect(() => {
    setEmail(accountEmail ?? '')
    setPhone(accountPhone ?? '')
    setNotes('')
  }, [accountEmail, accountUsername, accountPhone])

  const getSpreadLabel = (item: NutritionPromoItem) => {
    return `${item.maxSpread} Months`
  }

  const isSelected = (item: NutritionPromoItem) => {
    return selectedItems.find((a) => a.key === item.key) !== undefined
  }

  const getPrice = (price: number | null): number | string => {
    return price?.toLocaleString('en-NZ', { style: 'currency', currency: 'NZD' }) ?? 'POA'
  }

  const pageOneValidation = () => {
    if (selectedItems.length <= 0) {
      setPageOneError('Please select at least one promotion to continue')
      return false
    }

    setPageOneError(undefined)
    return true
  }

  const navigate = useNavigate()

  const pageTwoValidation = () => {
    let error = false

    const items = [...selectedItems]
    selectedItems.forEach((item) => {
      let errorMsg = undefined
      let errorType: 'total' | 'min' | 'max' | undefined = undefined
      const spreadsEntered = item.spreads.filter((a) => a.quantity > 0).length

      if (item.quantity < item.minQuantity) {
        errorMsg = `Minimum amount of ${item.minQuantity} required`
        errorType = 'total'
      } else if (item.minSpread && item.minSpread > spreadsEntered) {
        errorType = 'min'
        const val = item.minSpread - spreadsEntered
        const months = val > 1 ? 'months' : 'month'
        errorMsg = `The minimum spread is ${item.minSpread} months. Add at least ${val} more ${months}`
      } else if (item.maxSpread && item.maxSpread < spreadsEntered) {
        errorType = 'max'
        const val = spreadsEntered - item.maxSpread
        const months = val > 1 ? 'months' : 'month'
        errorMsg = `The maximum spread is ${item.maxSpread} months. Remove ${val} ${months}`
      }

      items[items.indexOf(item)] = {
        ...item,
        error: errorType,
        errorMsg: errorMsg,
      }
    })

    setSelectedItems(items)

    if (items.reduce((accumulator, item) => accumulator + (item.errorMsg ? 1 : 0), 0) > 0) {
      error = true
    }

    if (!acceptedTerms && !error) {
      setPageTwoError(' ')
      return false
    }

    if (error) {
      setPageTwoError('There are issues with your spread')
      return false
    }

    setPageTwoError(undefined)
    return true
  }

  const [submitLoading, setSubmitLoading] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string>()

  const clearState = () => {
    setPageIndex(0)
    setSelectedItems([])
    setAcceptedTerms(false)
    setSubmitLoading(false)
    setPhoneError(undefined)
    setEmailError(undefined)
    setSubmitError(undefined)
    setPageOneError(undefined)
    setPageTwoError(undefined)
    setPageThreeError(undefined)
  }

  const pageThreeValidation = () => {
    const _emailError = isInvalidEmail(email)
    setEmailError(_emailError)

    const _phoneError = isInvalidPhoneNumber(phone)
    setPhoneError(_phoneError)

    if (_emailError || _phoneError) {
      setPageThreeError('Please enter the correct details')
      setSubmitLoading(false)
      return false
    }

    setPageThreeError(undefined)
    setSubmitLoading(false)

    return true
  }

  const pageOne = React.useMemo(() => {
    return (
      <StepOne
        getPrice={getPrice}
        getSpreadLabel={getSpreadLabel}
        isSelected={isSelected}
        // Intentionally upping to MD
        isSm={isSm}
        promoDictionary={promoDictionary}
        selectedDepot={selectedDepot}
        selectedItems={selectedItems}
        setSelectedDepot={setSelectedDepot}
        setSelectedItems={setSelectedItems}
        uniqueLocations={uniqueLocations}
        startMonth={new Date(config.startMonth)}
      />
    )
  }, [promoItems, isSm, isMobile, selectedItems, pageOneError])

  const pageTwo = React.useMemo(() => {
    return (
      <StepTwo
        acceptedTerms={acceptedTerms}
        expanded={expanded}
        getPrice={getPrice}
        isSm={isMobile}
        pageTwoError={pageTwoError}
        selectedItems={selectedItems}
        setAcceptedTerms={setAcceptedTerms}
        setExpanded={setExpanded}
        setSelectedItems={setSelectedItems}
        startMonth={new Date(config.startMonth)}
      />
    )
  }, [promoItems, isSm, isMobile, selectedItems, expanded, acceptedTerms])

  const pageThree = React.useMemo(() => {
    const containsPOA = selectedItems.find((a) => a.price === null || a.price === 0)
    const containsPrice = selectedItems.find((a) => a.price !== null && a.price !== 0)
    const price = selectedItems
      .filter((a) => a.price)
      .reduce(
        //item.quantity = total
        (accumulator, item) => accumulator + item.quantity * (item.price ?? 0),
        0,
      )

    const pricePrefix = ''
    const priceDisplay = pricePrefix + getPrice(price)

    return (
      <StepThree
        containsPOA={containsPOA}
        containsPrice={containsPrice}
        email={email}
        emailError={emailError}
        isSm={isSm}
        notes={notes}
        phone={phone}
        phoneError={phoneError}
        priceDisplay={priceDisplay}
        selectedItems={selectedItems}
        setEmail={setEmail}
        setNotes={setNotes}
        setPhone={setPhone}
      />
    )
  }, [
    email,
    emailError,
    phone,
    phoneError,
    notes,
    selectedItems,
    pageThreeError,
    submitError,
    submitLoading,
  ])

  const dispatch = useAppDispatch()

  const onSubmit = () => {
    if (currentAccount) {
      const postOrder: PromotionOrderPayload = {
        accountNumber: currentAccount?.accountNumber,
        customerName: accountUsername ?? 'Username unknown',
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        email: email!,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        phone: phone!,
        notes: notes ?? '',
        products: selectedItems.map((e) => {
          return {
            key: e.key,
            name: e.name,
            quantity: e.quantity,
            price: e.price,
            location: e.location,
            spreads: e.spreads,
          }
        }),
      }
      setSubmitLoading(true)

      apiClient
        .post('/promo-order', postOrder)
        .then((result) => {
          if (result.status.toString().startsWith('2')) {
            clearState()
            handleClose()
            navigate('/home/promo-order-success')
          }
        })
        .catch((error: AxiosError<{ type: string; status: number }>) => {
          const errorMsg = error.response?.data.type
          if (error.response?.data.type === 'urn:errors:invalid-promo-price') {
            ;(async () => {
              await dispatch(getNutritionPromoInfo())
              setSubmitLoading(false)
              setPageThreeError(
                'Prices have updated. Please review the updated prices and resubmit',
              )
              setSubmitError(errorMsg)
            })()
          } else {
            setSubmitLoading(false)
            setPageThreeError('Unknown error, please try again in a few minutes')
            setSubmitError(errorMsg)
          }
        })
    }
  }

  const pages = React.useMemo(() => {
    return [
      {
        title: 'Select the promos available',
        component: pageOne,
        button: 'Continue',
        validation: pageOneValidation,
        error: pageOneError,
      },
      {
        title: 'Select the optimal spread for your chosen nutrition agreements.',
        component: pageTwo,
        button: 'Continue',
        validation: pageTwoValidation,
        error: pageTwoError,
      },
      {
        title: 'Submit your order',
        component: pageThree,
        button: 'Submit',
        validation: pageThreeValidation,
        error: pageThreeError,
      },
    ]
  }, [pageOne, pageTwo, pageThree, promoItems, isSm, selectedItems, expanded, acceptedTerms])

  const currentAction = React.useMemo(() => {
    if (submitError && pageIndex === pages.length - 1) {
      return (
        <Stack spacing={2} direction='row'>
          <Button
            sx={{ alignSelf: 'flex-end', marginLeft: 'auto', width: isSm ? '100%' : 'auto' }}
            variant='text'
            data-testid={TestIds.feat.drawdown.continueButton}
            onClick={() => {
              clearState()
            }}
          >
            Start again
          </Button>

          <Button
            sx={{ alignSelf: 'flex-end', marginLeft: 'auto', width: isSm ? '100%' : 'auto' }}
            variant='contained'
            data-testid={TestIds.feat.drawdown.continueButton}
            onClick={() => {
              setSubmitError(undefined)
              onSubmit()
            }}
          >
            Re-submit
          </Button>
        </Stack>
      )
    }
    if (submitLoading) {
      return (
        <Box ml={'auto'}>
          <Loader type='inline-flex' title='Submitting' />
        </Box>
      )
    }
    return (
      <Button
        sx={{ alignSelf: 'flex-end', marginLeft: 'auto', width: isSm ? '100%' : 'auto' }}
        variant='contained'
        data-testid={TestIds.feat.drawdown.continueButton}
        onClick={() => {
          if (pages[pageIndex].validation()) {
            if (pageIndex === pages.length - 1) {
              onSubmit()
            } else {
              setPageIndex((pageIndex + 1) % pages.length)
            }
          }
        }}
      >
        {pages[pageIndex].button}
      </Button>
    )
  }, [pages, pageIndex, submitLoading, submitError])

  if (open)
    return (
      <Modal
        fullWidth
        fullScreen={isMobile}
        maxWidth='md'
        paperProps={{
          sx: {
            height: '100%',
          },
        }}
        onClick={(e) => e.stopPropagation()}
        open={open}
        handleClose={handleClose}
        title='Fieldays® special prices'
        testId={TestIds.feat.declarations.modal}
        header={
          <Steppers
            maxSteps={pages.length}
            activeStep={pageIndex}
            title={pages[pageIndex].title}
            previousStep={previousIndex}
            setPageIndex={(value) => {
              let validationPassed = true
              for (let i = pageIndex; i < value; i++) {
                if (!pages[i].validation()) {
                  validationPassed = false
                  setPageIndex(i) // Set the page index to the page where validation failed
                  break // Stop the loop once validation fails
                }
              }
              if (validationPassed) {
                setPreviousIndex(pageIndex)
                setPageIndex(value)
              }
            }}
          />
        }
        footer={
          <Box display='flex' width='100%'>
            {pages[pageIndex].error && (
              <Typography variant='body1' color={colors.error500} sx={{ marginRight: 'auto' }}>
                {pages[pageIndex].error}
              </Typography>
            )}
            {currentAction}
          </Box>
        }
      >
        {pages[pageIndex].component}
      </Modal>
    )
}

export default PromoModal
